import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, BellRing, Loader2, Smartphone, Lock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import type { AlertRule } from '@/hooks/useAlerts';
import { useToast } from '@/hooks/useToast';

/**
 * Push-notification enrollment card for the Alerts page. Pro-gated. Handles
 * every support state (unsupported, iOS-needs-install, unconfigured, denied,
 * default, subscribed) with an honest message rather than a dead toggle.
 */
export function PushNotificationCard({
  rules,
  isPro,
}: {
  rules: AlertRule[];
  isPro: boolean;
}) {
  const { status, busy, subscribe, unsubscribe, syncRules, sendTest } =
    usePushNotifications();
  const { toast } = useToast();
  const [testing, setTesting] = useState(false);

  // Keep the server-side copy of this device's rules in sync whenever the
  // user edits thresholds or toggles rules (no-op unless already subscribed).
  useEffect(() => {
    if (status === 'subscribed') syncRules(rules);
  }, [rules, status, syncRules]);

  // Pro gate first — push is a Pro feature.
  if (!isPro) {
    return (
      <Card className="mt-4 border-primary/30 bg-primary/5">
        <CardContent className="flex items-center justify-between gap-4 p-4">
          <div className="flex items-start gap-3">
            <BellRing className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div>
              <p className="text-sm font-medium">Push notifications</p>
              <p className="text-sm text-muted-foreground">
                Get alerted on your device the moment a threshold is crossed —
                even when the app is closed. Available on Pro.
              </p>
            </div>
          </div>
          <Button asChild size="sm" className="shrink-0">
            <Link to="/pricing">Upgrade</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  const handleEnable = async () => {
    const ok = await subscribe(rules);
    toast(
      ok
        ? { title: 'Push notifications enabled', description: 'This device will now receive alerts.' }
        : { title: 'Could not enable notifications', description: 'Permission was not granted.', variant: 'destructive' },
    );
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const ok = await sendTest();
      toast(
        ok
          ? { title: 'Test notification sent', description: 'It should arrive on this device in a few seconds.' }
          : { title: 'Could not send test', description: 'Check that notifications are still allowed, then try again.', variant: 'destructive' },
      );
    } finally {
      setTesting(false);
    }
  };

  const handleDisable = async () => {
    await unsubscribe();
    toast({ title: 'Push notifications disabled', description: 'This device will no longer receive alerts.' });
  };

  // Non-actionable informational states.
  if (status === 'unsupported' || status === 'ios-needs-install' || status === 'unconfigured') {
    const message =
      status === 'ios-needs-install' ? (
        <>
          On iPhone/iPad, add BCH Radar to your Home Screen first (Share →{' '}
          <span className="text-foreground/80">Add to Home Screen</span>), then
          open it from there to enable push.
        </>
      ) : status === 'unconfigured' ? (
        <>Push delivery isn&apos;t configured on this deployment yet.</>
      ) : (
        <>This browser doesn&apos;t support push notifications.</>
      );
    return (
      <Card className="mt-4">
        <CardContent className="flex items-start gap-3 p-4 text-sm text-muted-foreground">
          <Smartphone className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium text-foreground">Push notifications</p>
            <p>{message}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const subscribed = status === 'subscribed';

  return (
    <Card className="mt-4 border-primary/20">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            {subscribed ? (
              <BellRing className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            ) : (
              <Bell className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
            )}
            <div>
              <p className="text-sm font-medium">Push notifications</p>
              <p className="text-sm text-muted-foreground">
                {status === 'denied' ? (
                  <>
                    Notifications are blocked in your browser settings. Re-enable
                    them for this site, then toggle this on.
                  </>
                ) : subscribed ? (
                  <>This device receives alerts (checked every ~30 min).</>
                ) : (
                  <>Enable alerts on this device. Checked every ~30 min.</>
                )}
              </p>
            </div>
          </div>
          {busy ? (
            <Loader2 className="h-5 w-5 shrink-0 animate-spin text-muted-foreground" />
          ) : status === 'denied' ? (
            <Lock className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <Switch
              checked={subscribed}
              onCheckedChange={(v) => (v ? handleEnable() : handleDisable())}
              aria-label="Toggle push notifications"
            />
          )}
        </div>

        {subscribed && (
          <div className="mt-3 flex items-center justify-between gap-3 border-t border-border/60 pt-3">
            <p className="text-xs text-muted-foreground">
              Verify delivery without waiting for a market move.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 gap-2"
              disabled={testing}
              onClick={handleTest}
            >
              {testing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <BellRing className="h-4 w-4" />
              )}
              Send test
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
